import React from "react";
import type { IResponse } from "@redbelly-tokeniser/mystake-api-sdk";
import { YIELD_ACCRUAL_NOT_AVAILABLE } from "@redbelly-tokeniser/mystake-validator-public";
import { errorMessage } from "../../../../commons/errorHandler";

export interface IYieldRead<T> {
  data: T | null;
  isLoading: boolean;
  loadError: string | null;
  /** The fund is not offered yield. Not an error: the screen renders as though yield does not exist. */
  notOffered: boolean;
  reload: () => void;
}

/**
 * A mount-time read of one of the yield routes. A refusal is reported as a failure rather than as empty
 * data, except the capability refusal, which means yield does not exist here. A late response after the
 * screen has moved on is ignored, and the loading state always clears.
 *
 * Pass `load` as null to keep the hook inert, with no request made and nothing loading.
 *
 * When `deps` change, the previous read's result is withheld until the new one settles, so a screen
 * never shows one class's figures under another class's name. A `reload` with the same deps keeps the
 * current result visible while it refreshes.
 */
export const useYieldRead = <T>(
  load: (() => Promise<IResponse<T>>) | null,
  deps: unknown[],
  fallbackMessage: string
): IYieldRead<T> => {
  const [data, setData] = React.useState<T | null>(null);
  const [isLoading, setIsLoading] = React.useState(load !== null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [notOffered, setNotOffered] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);
  const [settledKey, setSettledKey] = React.useState<string | null>(null);

  // Held in a ref so a caller can pass it inline; as a dependency it would refire every render.
  const loadRef = React.useRef(load);
  loadRef.current = load;
  const enabled = load !== null;
  const readKey = JSON.stringify([enabled, ...deps]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  React.useEffect(() => {
    let mounted = true;

    const run = async (): Promise<void> => {
      const { current } = loadRef;
      if (!current) {
        setData(null);
        setLoadError(null);
        setNotOffered(false);
        setIsLoading(false);
        setSettledKey(readKey);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setNotOffered(false);
      try {
        const response = await current();
        if (!mounted) {
          return;
        }
        if (!response?.status && response?.message === YIELD_ACCRUAL_NOT_AVAILABLE) {
          setData(null);
          setNotOffered(true);
          return;
        }
        if (!response?.status) {
          throw new Error(response?.message || fallbackMessage);
        }
        setNotOffered(false);
        setData(response.payload ?? null);
      } catch (error) {
        if (mounted) {
          setData(null);
          setLoadError(errorMessage(error, fallbackMessage));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
          setSettledKey(readKey);
        }
      }
    };

    run().catch(() => undefined);

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fallbackMessage, reloadToken, ...deps]);

  const settled = settledKey === readKey;
  return {
    data: settled ? data : null,
    isLoading: isLoading || (enabled && !settled),
    loadError: settled ? loadError : null,
    notOffered: settled && notOffered,
    reload,
  };
};
